/* eslint-disable camelcase */
import { useTranslation } from 'react-i18next';

import { PlotGroup } from '@/api/rest/resources/types/plotGroup';
import { WidgetType } from '@/api/rest/resources/types/widget';
import { useAllPlotsForOrganization } from '@/pages/shared/hooks/usePlotsForOrganization';
import { isNullish } from '@/utils/booleans';

const MAX_ADDITIONAL_PLOT_GROUPS_DISPLAY = 5;

export const useAdditionalPlotsFilterPillWidget = ({
  plotGroup,
  onRemove,
}: {
  plotGroup: PlotGroup;
  onRemove: (plotId: string) => void | undefined;
}) => {
  const { t } = useTranslation();
  const { additionalPlots: activeAdditionalPlots, extraPlots: extraAdditionalPlots } = useGetAdditionalPlots(plotGroup);
  const activeAdditionalPlotsPills = activeAdditionalPlots.map((plot) => ({
    name: `plot_group_filters_management_panel_pill_additional_plot_${plot.id}`,
    type: WidgetType.a_pill,
    visible: activeAdditionalPlots.length > 0,
    relatedData: {
      className: 'bg-bg-light-grey typography-body2 text-text-secondary',
    },
    widgets: [
      {
        name: `plot_group_filters_management_panel_pill_text_additional_plot_${plot.id}`,
        type: WidgetType.a_text,
        visible: true,
        relatedData: {
          text: `${t('global.widget.filters.sections.plot')}: ${plot.name}`,
        },
      },
      {
        name: `plot_group_filters_management_panel_pill_button_additional_plot_${plot.id}`,
        type: WidgetType.a_button,
        visible: !isNullish(onRemove),
        relatedData: {
          variant: 'text',
          className: 'w-fit p-0 bg-active-54 text-bg-light-grey hover:text-active-54',
          size: 'small',
          onClick: !isNullish(onRemove) ? () => onRemove(plot.id) : undefined,
        },
        widgets: [
          {
            name: `plot_group_filters_management_panel_pill_button_icon_additional_plot_${plot.id}`,
            type: WidgetType.a_icon,
            visible: true,
            relatedData: {
              icon: 'ri-close-line',
              className: '',
            },
          },
        ],
      },
    ],
  }));

  const extraAdditionalPlotsPill = {
    name: 'plot_group_filters_management_panel_pill_extra_additional_plots',
    type: WidgetType.a_pill,
    visible: extraAdditionalPlots > 0,
    relatedData: {
      className: 'bg-bg-light-grey typography-body2 text-text-secondary',
    },
    widgets: [
      {
        name: 'plot_group_filters_management_panel_pill_extra_additional_plots_text',
        type: WidgetType.a_text,
        visible: true,
        relatedData: {
          text: t('global.widget.filters.sections.extraAdditionalPlots', { count: extraAdditionalPlots }),
        },
      },
    ],
  };

  return {
    name: 'plot_group_filters_management_panel_additional_plots',
    type: WidgetType.a_stack,
    visible: activeAdditionalPlotsPills.length > 0,
    relatedData: {
      className: 'flex-row flex-wrap gap-2',
    },
    widgets: [...activeAdditionalPlotsPills, extraAdditionalPlotsPill],
  };
};

const useGetAdditionalPlots = (plotGroup: PlotGroup) => {
  const { additional_plot_ids = [] } = plotGroup;

  const plots = useAllPlotsForOrganization(undefined, undefined, plotGroup.id ?? undefined, undefined).data;
  const additionalPlotsData = plots?.results.filter((plot) => additional_plot_ids.includes(plot.id)) ?? [];

  return {
    additionalPlots: additionalPlotsData.slice(0, MAX_ADDITIONAL_PLOT_GROUPS_DISPLAY),
    extraPlots:
      additionalPlotsData.length > MAX_ADDITIONAL_PLOT_GROUPS_DISPLAY
        ? additionalPlotsData.length - MAX_ADDITIONAL_PLOT_GROUPS_DISPLAY
        : 0,
  };
};
